"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useInsertionEffect,
  useMemo,
  useRef,
  type ComponentPropsWithRef,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from "react";
import { withRef } from "@/components/wingo/lib/with-ref";

// the navigation contract every nav component shares: links rendered through the app's router without importing it,
// which item is active, one shape for nav items, and the context an app shell gives its top bar and tab bar

// ---------------------------------------------------------------- types

// how an item's href matches the current path. prefix: /clients is active on /clients/42 too. "/" is always exact
export type NavMatch = "exact" | "prefix";

// one navigation entry, shared by sidebar, bottom tab bar, nav tabs, nav menu and the command menu's "Go to" group,
// so an app defines its navigation once
export type NavItem = {
  // stable key and the value `value` selects; default: href, else the label when it is a string
  id?: string;
  label: ReactNode;
  // plain text for filtering, tooltips and aria when label is not a string
  textValue?: string;
  href?: string;
  icon?: ReactNode;
  // shown instead of icon while active (a filled glyph in a tab bar)
  activeIcon?: ReactNode;
  // a number renders a count badge, a node renders as is
  badge?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  // opens in a new tab with rel="noopener noreferrer"; default: absolute urls to another origin
  external?: boolean;
  // default "prefix"
  match?: NavMatch;
  // runs on click (before navigation); items without href act as buttons
  onSelect?: (event: MouseEvent<HTMLElement>) => void;
  // children: nested sidebar items, nav menu panels
  items?: NavItem[];
  // extra search terms for filters and the command menu
  keywords?: string[];
  // a key combo in mod+k notation, shown as a hint
  shortcut?: string;
};

export type NavGroup = {
  id?: string;
  label?: ReactNode;
  items: NavItem[];
  // the group label is a toggle that folds the group
  collapsible?: boolean;
  defaultOpen?: boolean;
};

// ---------------------------------------------------------------- matching

const ABSOLUTE = /^[a-z][a-z0-9+.-]*:/i;

// mailto:, tel:, protocol-relative and absolute urls to another origin
export function isExternalHref(href: string): boolean {
  if (href.startsWith("//")) return true;
  if (!ABSOLUTE.test(href)) return false;
  if (typeof window === "undefined") return true;
  try {
    return new URL(href).origin !== window.location.origin;
  } catch {
    return true;
  }
}

// the path part of an href, without query, hash, origin or trailing slash ("/" stays "/")
export function normalizePath(href: string): string {
  let path = href;
  if (ABSOLUTE.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return path;
    }
  }
  path = path.split(/[?#]/)[0] || "/";
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

// true when href points at currentPath (exact) or one of its ancestors (prefix). "/" only matches itself
export function matchPath(href: string | undefined, currentPath: string | undefined, match: NavMatch = "prefix"): boolean {
  if (!href || currentPath === undefined || isExternalHref(href)) return false;
  const target = normalizePath(href);
  const current = normalizePath(currentPath);
  if (target === current) return true;
  if (match === "exact" || target === "/") return false;
  return current.startsWith(`${target}/`);
}

// the key an item is tracked by: id, else href, else a string label, else its position
export function navItemKey(item: NavItem, index = 0): string {
  return item.id ?? item.href ?? (typeof item.label === "string" ? item.label : item.textValue ?? `item-${index}`);
}

// the item's text for filters, tooltips and aria labels
export function navItemText(item: NavItem): string {
  return typeof item.label === "string" ? item.label : (item.textValue ?? item.id ?? "");
}

// the items of every group, in order (the input the helpers below take)
export function groupItems(groups: readonly NavGroup[]): NavItem[] {
  return groups.flatMap((group) => group.items);
}

// every item of a tree, parents before their children
export function flattenNavItems(items: readonly NavItem[]): NavItem[] {
  const out: NavItem[] = [];
  const walk = (list: readonly NavItem[]) => {
    for (const item of list) {
      out.push(item);
      if (item.items?.length) walk(item.items);
    }
  };
  walk(items);
  return out;
}

// the active item and its ancestors ([parent, child]) or null. an explicit value (an item key) wins; otherwise the
// item whose href matches currentPath most specifically (the longest path) is active. groups: pass groupItems(groups)
export function findActivePath(
  items: readonly NavItem[],
  { value, currentPath }: { value?: string | null; currentPath?: string },
): NavItem[] | null {
  const byValue = value != null && value !== "";
  let best: NavItem[] | null = null;
  let bestLength = -1;
  const walk = (list: readonly NavItem[], trail: NavItem[]): NavItem[] | null => {
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const path = [...trail, item];
      if (byValue && navItemKey(item, i) === value) return path;
      if (!byValue && item.href && matchPath(item.href, currentPath, item.match)) {
        const length = normalizePath(item.href).length;
        if (length > bestLength) {
          best = path;
          bestLength = length;
        }
      }
      if (item.items?.length) {
        const hit = walk(item.items, path);
        if (hit) return hit;
      }
    }
    return null;
  };
  return walk(items, []) ?? best;
}

// ---------------------------------------------------------------- links

// anything that renders an anchor from an href: next/link's Link, react-router's Link, or "a"
export type LinkComponentProps = Omit<ComponentPropsWithRef<"a">, "href"> & { href: string };
export type LinkComponent = ComponentType<LinkComponentProps> | "a";

type NavigationContextValue = {
  component: LinkComponent;
  currentPath?: string;
  // tells onNavigate and every useNavigateListener that an in-app navigation starts
  notify: (href: string) => void;
  subscribe: (listener: (href: string) => void) => () => void;
  // the router's push, for navigations without an anchor (a command menu item picked with Enter)
  push?: (href: string) => void;
};

const NavigationContext = createContext<NavigationContextValue | null>(null);

export type LinkProviderProps = {
  // the app's router link; every NavLink inside renders through it. default "a"
  component?: LinkComponent;
  // the current pathname (next: usePathname()); nav components use it to find the active item
  currentPath?: string;
  // runs when a NavLink starts an in-app navigation (a plain left click on an internal link)
  onNavigate?: (href: string) => void;
  // the router's push (next: useRouter().push), used by useNavigate for navigations without an anchor. default: a
  // full page load through window.location.assign
  navigate?: (href: string) => void;
  children?: ReactNode;
};

// set once near the root, in a client component:
//   "use client"
//   export function Providers({ children }) {
//     const router = useRouter()
//     return <LinkProvider component={Link} currentPath={usePathname()} navigate={router.push}>{children}</LinkProvider>
//   }
export function LinkProvider({ component = "a", currentPath, onNavigate, navigate, children }: LinkProviderProps) {
  const listeners = useRef(new Set<(href: string) => void>());
  const latest = useRef({ onNavigate, navigate });
  useInsertionEffect(() => {
    latest.current = { onNavigate, navigate };
  });
  const notify = useCallback((href: string) => {
    latest.current.onNavigate?.(href);
    listeners.current.forEach((listener) => listener(href));
  }, []);
  const push = useCallback((href: string) => {
    const go = latest.current.navigate;
    if (go) go(href);
    else window.location.assign(href);
  }, []);
  const subscribe = useCallback((listener: (href: string) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);
  const value = useMemo(() => ({ component, currentPath, notify, subscribe, push }), [component, currentPath, notify, subscribe, push]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

// the link component to render: the prop, else the provider's, else "a"
export function useLinkComponent(component?: LinkComponent): LinkComponent {
  const provided = useContext(NavigationContext)?.component;
  return component ?? provided ?? "a";
}

// the current path: the prop, else the provider's (undefined when neither knows it)
export function useCurrentPath(currentPath?: string): string | undefined {
  const provided = useContext(NavigationContext)?.currentPath;
  return currentPath ?? provided;
}

// runs listener whenever a NavLink inside the provider starts an in-app navigation (a route progress bar starts here)
export function useNavigateListener(listener: (href: string) => void) {
  const context = useContext(NavigationContext);
  const latest = useRef(listener);
  useInsertionEffect(() => {
    latest.current = listener;
  });
  const subscribe = context?.subscribe;
  useEffect(() => subscribe?.((href) => latest.current(href)), [subscribe]);
}

// navigates without an anchor (a command menu or search result picked with Enter): external urls open in a new tab,
// in-app paths notify listeners (a progress bar starts) and go through the provider's navigate, else a full page load
export function useNavigate() {
  const context = useContext(NavigationContext);
  return useCallback(
    (href: string, options?: { external?: boolean }) => {
      if (options?.external ?? isExternalHref(href)) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      if (context) {
        context.notify(href);
        context.push?.(href);
      } else window.location.assign(href);
    },
    [context],
  );
}

export type NavLinkProps = Omit<ComponentPropsWithRef<"a">, "href"> & {
  href: string;
  // overrides the provider's link component for this link
  component?: LinkComponent;
  // sets aria-current and data-active
  active?: boolean;
  // the aria-current value while active: "page" for navigation, "step" in steppers, "location" in breadcrumbs
  current?: "page" | "step" | "location" | "true";
  // renders a link without href: not focusable, aria-disabled, clicks ignored
  disabled?: boolean;
  // new tab + rel="noopener noreferrer"; default: absolute urls to another origin
  external?: boolean;
};

function plainClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

// an anchor rendered through the app's router link, with aria-current while active, a real disabled state and safe
// external links. style it like any <a>; data-active and data-disabled are there for selectors
function NavLinkRender({
  href,
  component,
  active = false,
  current = "page",
  disabled = false,
  external,
  target,
  rel,
  onClick,
  children,
  ...props
}: NavLinkProps) {
  const context = useContext(NavigationContext);
  const Link = useLinkComponent(component);
  const isExternal = external ?? isExternalHref(href);
  const shared = {
    "data-slot": "nav-link",
    "data-active": active ? "" : undefined,
    "aria-current": active ? current : undefined,
    ...props,
  };

  if (disabled) {
    return (
      <a {...shared} aria-disabled="true" data-disabled="" tabIndex={-1} onClick={(event) => event.preventDefault()}>
        {children}
      </a>
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || isExternal || (target && target !== "_self") || !plainClick(event)) return;
    context?.notify(href);
  };

  if (isExternal) {
    const web = /^(https?:)?\/\//i.test(href);
    return (
      <a
        {...shared}
        href={href}
        target={target ?? (web ? "_blank" : undefined)}
        rel={rel ?? (web ? "noopener noreferrer" : undefined)}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  // createElement: the link type comes from context (the app's router link), not from a component made in render
  return createElement(Link, { ...shared, href, target, rel, onClick: handleClick }, children);
}

// ---------------------------------------------------------------- app shell bridge

// what an app shell shares with the bars inside it, so a top bar can render the menu button and a tab bar knows it is
// laid out by the shell, without either importing the shell
export type NavShellState = {
  // below the shell's mobile breakpoint: the sidebar lives in a drawer
  isMobile: boolean;
  // desktop icon-only mode
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  // the phone drawer
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  // collapses on desktop, opens / closes the drawer on phones
  toggleSidebar: () => void;
  // the shell renders a sidebar at all
  hasSidebar: boolean;
  // window: the document scrolls (bars pin with position fixed / sticky). content: the shell's main column scrolls
  // (bars inside it are laid out by the shell; a tab bar renders static under the column)
  scroll: "window" | "content";
  // the shell's own labels, reused by the bars' buttons
  labels: { openMenu: string; closeMenu: string; collapse: string; expand: string };
};

const NavShellContext = createContext<NavShellState | null>(null);

export type NavShellProviderProps = { value: NavShellState; children?: ReactNode };

export function NavShellProvider({ value, children }: NavShellProviderProps) {
  return <NavShellContext.Provider value={value}>{children}</NavShellContext.Provider>;
}

// the enclosing app shell's state, or null outside one
export function useNavShell(): NavShellState | null {
  return useContext(NavShellContext);
}

// React 18: ref-ul (ex. de la un DropdownMenuItem asChild, care își înregistrează rândurile prin ref) ajunge la link
// doar prin forwardRef (vezi lib/with-ref.ts)
export const NavLink = withRef(NavLinkRender, "NavLink");
